/**
 * GPC threshold "next steps" message composer.
 *
 * The backend emits the STRUCTURED FACTS a threshold decision is derived from
 * (flags, sourcing path, buy type, micro-purchase threshold) and the frontend
 * owns the user-facing prose — the same split the app already uses for errors
 * (`error_code -> USER_FACING_ERROR_MESSAGES`).
 *
 * The four copy blocks below are relocated BYTE-IDENTICAL from the backend
 * `_NEXT_STEPS_*` builders (`gpc_orchestrator.py`). Old persisted Path-B flows
 * that predate this split lack a micro-purchase threshold; they recompose a
 * fresh, buy-type-aware generic phrasing (no dollar figure) rather than falling
 * back to any stored `next_steps` string.
 */

export type GpcNextStepsInput = {
  needMoreInfo: boolean;
  outOfScope: boolean;
  sourcingPath: string | null; // 'open_market' = Path A
  buyType: string | null;
  microPurchaseThreshold: number | null;
};

const PATH_A =
  'Proceed open-market with the documented (searched) prices. This is below the micro-purchase threshold: no approval routing and no DD/DAF 2579 are required. STILL REQUIRED on every purchase: an 889 covered-telecom representation form from the chosen vendor, a GPC Abstract with an AO control number, and price reasonableness from the documented prices.';

const OUT_OF_SCOPE =
  'This purchase exceeds the $25K v1 ceiling — the Expanded-Use GPC band ($25K–$350K) is out of scope; route to the local CONS Expanded Use Program.';

const NEED_MORE_INFO =
  'Need more info to determine the threshold: a searched unit price, a quantity, and a buy type (supply/service/construction) are all required.';

function composePathB(buyType: string | null, microPurchaseThreshold: number | null): string {
  const mptPhrase =
    microPurchaseThreshold != null
      ? `the $${microPurchaseThreshold.toLocaleString('en-US')} micro-purchase threshold for ${buyType}`
      : buyType
        ? `the micro-purchase threshold for ${buyType}`
        : 'the micro-purchase threshold';
  return `This purchase exceeds ${mptPhrase}: mandatory sources apply. An 889 covered-telecom representation is required, at least 3 quotes (or a mandated source) must document price reasonableness, and it must be routed to eCARS approval (Ms. Clark, the GPC PM, manually approves the $15K–$25K band). A DD/DAF 2579 (small-business coordination) is required only if the purchase is over $15K AND the GSA vendor is not a small business.`;
}

export function composeGpcNextSteps(i: GpcNextStepsInput): string {
  if (i.needMoreInfo) return NEED_MORE_INFO;
  if (i.outOfScope) return OUT_OF_SCOPE;
  if (i.sourcingPath === 'open_market') return PATH_A;
  // Path B (mandatory source). Old persisted flows lacking the MPT recompose a
  // fresh, buy-type-aware generic phrasing rather than reusing any stored prose.
  return composePathB(i.buyType, i.microPurchaseThreshold);
}
